import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../../modules/auth/services/auth/auth.service';
import { Capacitor } from '@capacitor/core';
import { XdsButtonComponent } from '../../../shared/ui';

/**
 * Full-screen blocking dialog shown when subscription is suspended (day 45+).
 * Cannot be closed via backdrop or escape.
 *
 * Admin view: Subscribe, Export Data, Delete Account
 * Employee view: "Account inactive, contact employer"
 * iOS: Subscribe button hidden (App Store Guideline 3.1.1)
 */
@Component({
  selector: 'app-payment-wall',
  standalone: true,
  imports: [CommonModule, MatDialogModule, TranslateModule, XdsButtonComponent],
  template: `
    <div class="payment-wall">
      <div class="pw-icon-container">
        <div class="pw-icon">&#128274;</div>
      </div>

      <!-- Admin View -->
      <ng-container *ngIf="isAdmin">
        <h2 class="pw-title">{{ 'PAYMENT_WALL.TITLE_ADMIN' | translate }}</h2>
        <p class="pw-message">{{ 'PAYMENT_WALL.MESSAGE_ADMIN' | translate }}</p>

        <div class="pw-actions">
          <xds-button *ngIf="!isIos" variant="primary" [block]="true" (clicked)="goToSubscription()">
            {{ 'PAYMENT_WALL.SUBSCRIBE' | translate }}
          </xds-button>
          <!-- Kein Ziel in der App: /settings/company gibt es nicht. Auf iOS wäre ein toter Knopf
               auf dem Sperrbildschirm ein Prüfbefund (App Review). -->
          <xds-button *ngIf="!isIos" variant="secondary" [block]="true" (clicked)="goToExport()">
            {{ 'PAYMENT_WALL.EXPORT_DATA' | translate }}
          </xds-button>
          <xds-button variant="danger" [block]="true" (clicked)="goToDeleteAccount()">
            {{ 'PAYMENT_WALL.DELETE_ACCOUNT' | translate }}
          </xds-button>
        </div>
      </ng-container>

      <!-- Employee View -->
      <ng-container *ngIf="!isAdmin">
        <h2 class="pw-title">{{ 'PAYMENT_WALL.TITLE_EMPLOYEE' | translate }}</h2>
        <p class="pw-message">{{ 'PAYMENT_WALL.MESSAGE_EMPLOYEE' | translate }}</p>
      </ng-container>
    </div>
  `,
  styles: [`
    .payment-wall {
      text-align: center;
      padding: 40px 24px;
    }

    .pw-icon-container {
      display: flex;
      justify-content: center;
      margin-bottom: 20px;
    }

    .pw-icon {
      width: 72px;
      height: 72px;
      border-radius: 50%;
      background: #fef2f2;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 36px;
    }

    .pw-title {
      font-size: 20px;
      font-weight: 700;
      color: #111827;
      margin: 0 0 12px 0;
    }

    .pw-message {
      font-size: 15px;
      color: #6b7280;
      line-height: 1.5;
      margin: 0 0 28px 0;
    }

    .pw-actions {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    /* .pw-btn* entfernt — CTAs laufen jetzt über <xds-button variant> (Welle 5). */
  `]
})
export class PaymentWallComponent {
  isAdmin: boolean;
  isIos = Capacitor.getPlatform() === 'ios';

  constructor(
    private dialogRef: MatDialogRef<PaymentWallComponent>,
    private authService: AuthService,
    private router: Router
  ) {
    this.isAdmin = this.authService.isAdmin;
  }

  goToSubscription(): void {
    this.dialogRef.close();
    this.router.navigateByUrl('/settings/subscription');
  }

  goToExport(): void {
    this.dialogRef.close();
    this.router.navigateByUrl('/settings/company');
  }

  /**
   * Bis zum 24.09.2026 führte der Knopf nach `/settings/account` — eine Route, die es nicht
   * gibt. Apple verlangt einen funktionierenden Weg zur Kontolöschung (Guideline 5.1.1(v)).
   */
  goToDeleteAccount(): void {
    this.dialogRef.close();
    this.router.navigateByUrl('/settings/account-deletion');
  }
}
